import { useState, useEffect, useCallback } from 'react';
import {
  ShoppingBag,
  DollarSign,
  Clock,
  Package,
  TrendingUp,
} from 'lucide-react';
import { apiGet } from '../lib/api';
import { formatNTD } from '@yiweiwu/shared';
import type { DashboardData } from '../lib/types';
import { KpiCard } from '../components/KpiCard';
import { StatusBadge } from '../components/StatusBadge';

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await apiGet<DashboardData>('/dashboard');
      setData(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '載入儀表板失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-brand-bake border-t-brand-gold" />
        <p className="mt-4 text-gray-400">載入儀表板...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={fetchData}
          className="px-4 py-2 rounded-lg bg-brand-gold text-white text-sm hover:bg-brand-gold/90"
        >
          重新載入
        </button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div>
      {/* 頁首 */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-brand-cocoa font-heading">
          儀表板
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          訂單概覽與營運摘要
        </p>
      </div>

      {/* KPI 卡片 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard
          title="今日訂單數"
          value={data.todayOrders}
          icon={ShoppingBag}
          subtitle="今日新進訂單"
        />
        <KpiCard
          title="今日營收"
          value={formatNTD(data.todayRevenue)}
          icon={DollarSign}
          subtitle="今日訂單總額"
        />
        <KpiCard
          title="待處理"
          value={data.pendingActions}
          icon={Clock}
          subtitle={
            data.pendingActions > 0
              ? `${data.pendingActions} 筆訂單需處理`
              : '目前無待處理事項'
          }
        />
        <KpiCard
          title="總訂單數"
          value={Object.values(data.statusCounts).reduce((a, b) => a + b, 0)}
          icon={TrendingUp}
          subtitle="系統內全部訂單"
        />
      </div>

      {/* 狀態分布 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 各狀態數量 */}
        <div className="bg-white rounded-xl border border-brand-bake p-5 shadow-sm">
          <h3 className="text-base font-semibold text-brand-cocoa mb-4">
            依狀態分佈
          </h3>
          <div className="space-y-3">
            {Object.entries(data.statusCounts).map(([status, count]) => (
              <div
                key={status}
                className="flex items-center justify-between py-2"
              >
                <div className="flex items-center gap-3">
                  <StatusBadge status={status} />
                </div>
                <span className="text-2xl font-bold text-brand-caramel font-heading">
                  {count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 快速導覽 */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-brand-bake p-5 shadow-sm">
            <h3 className="text-base font-semibold text-brand-cocoa mb-3">
              快速操作
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <a
                href="/admin/orders?status=paid"
                className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 transition-colors"
              >
                <Package className="h-4 w-4" />
                待備貨訂單
              </a>
              <a
                href="/admin/orders?status=preparing"
                className="flex items-center gap-2 p-3 rounded-lg bg-purple-50 text-purple-700 text-sm font-medium hover:bg-purple-100 transition-colors"
              >
                <Package className="h-4 w-4" />
                備貨中訂單
              </a>
              <a
                href="/admin/orders?status=shipped"
                className="flex items-center gap-2 p-3 rounded-lg bg-teal-50 text-teal-700 text-sm font-medium hover:bg-teal-100 transition-colors"
              >
                <Package className="h-4 w-4" />
                配送中訂單
              </a>
              <a
                href="/admin/orders"
                className="flex items-center gap-2 p-3 rounded-lg bg-brand-cream text-brand-caramel text-sm font-medium hover:bg-brand-bake/50 transition-colors"
              >
                <ShoppingBag className="h-4 w-4" />
                全部訂單
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
